<template>
  <div>
    <v-card
      flat
      class="query-table-container elevation-2 border-0 mb-4 rounded-lg"
      :style="{ '--query-accent': accentColor, '--query-accent-soft': accentSoftColor, '--query-accent-bg': accentBgColor }"
    >
      <v-toolbar flat class="query-toolbar" height="56">
        <v-col cols="8" class="d-flex align-center subheading pb-2 pl-6">
          <quick-rename-input
            :value="schema.name"
            @onChange="onBlurName"
            density="compact"
            :max-length="30"
            :disabled="isWorkflowDisabled"
          />
        </v-col>
        <v-spacer />
        <v-col xs4 class="text-right pb-2 pr-6 d-flex flex-column align-end justify-center ga-1">
          <select-entity :entityId="schema.entityId" @onSelectEntity="onSelectEntity">
            <v-chip
              density="compact"
              size="small"
              class="cursor-pointer entity-chip"
              :disabled="isWorkflowDisabled && !linkedEntity"
            >
              <v-icon start size="12">mdi-database</v-icon>
              <span>{{ linkedEntity ? linkedEntity.name : 'Select Entity' }}</span>
            </v-chip>
          </select-entity>
          <toggle
            mandatory
            class="cardinality-toggle"
            :value="schema.cardinality || 'one-to-many'"
            :options="QUERY_CARDINALITY_OPTIONS"
            :disabled="isWorkflowDisabled"
            @update:modelValue="(val) => setReadModelCardinality(schema.id, val)"
          />
        </v-col>
      </v-toolbar>

      <div
        class="query-description"
        :class="{
          'query-description--empty': !schema.description,
          'query-description--invalid': schemaDescriptionInvalid
        }"
        :contenteditable="!isWorkflowDisabled"
        :data-placeholder="isWorkflowDisabled ? 'No description' : 'Add a short description…'"
        spellcheck="false"
        @input="checkSchemaDescriptionLength"
        @paste="onPasteSchemaDescription"
        @blur="onBlurSchemaDescription"
        @keydown.enter.prevent="$event.target.blur()"
        @keydown.esc="restoreSchemaDescription($event)"
      >
        {{ schema.description || '' }}
      </div>
      <div v-if="schemaDescriptionInvalid" class="query-description-error">
        Description is too long ({{ schemaDescriptionLength }}/{{ MAX_DESCRIPTION_LENGTH }} characters). Changes won't be saved
        until shortened.
      </div>

      <v-table class="query-fields-table rounded-0 elevation-1">
        <thead>
          <tr>
            <th class="name-header">Name</th>
            <th class="description-header">Description</th>
            <th class="entity-header">Related Entity</th>
            <th class="actions-header"></th>
          </tr>
        </thead>
        <query-field-rows
          v-for="(field, fieldIndex) in (schema.fields ?? [])"
          :key="field.name"
          :top-field="field"
          :field-index="fieldIndex"
          :top-field-is-last="fieldIndex === (schema.fields ?? []).length - 1"
          :schema="schema"
          :is-workflow-disabled="isWorkflowDisabled"
        />
      </v-table>
    </v-card>
    <v-btn
      v-if="!isWorkflowDisabled"
      rounded="lg"
      size="large"
      variant="flat"
      color="primary"
      @click="onAddField"
    >
      + Add field
    </v-btn>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import QuickRenameInput from '@/components/QuickRenameInput'
import QueryFieldRows from '@/components/QueryFieldRows.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import copyPasteHelpers from '@/helpers/copyPasteHelpers'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelActions from '@/composables/useReadModelActions'
import {
  SchemaType,
  QUERY_CARDINALITY_OPTIONS,
  MAX_DESCRIPTION_LENGTH
} from '@/constants/schema'

const { schema } = defineProps({
  schema: Object
})

const store = useStore()

const {
  blurReadModelName,
  blurReadModelDescription,
  setReadModelCardinality
} = useReadModelActions()

const {
  selectSourceEntity,
  addSchemaField
} = useDomainModel()

const workflow = computed(() => store.state.workflow.workflow)
const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(cardTypes.value))

const rgbParts = computed(() => {
  const color = queryCardType.value?.color
  if (!color) {
    return null
  }

  return `${color.red}, ${color.green}, ${color.blue}`
})

const accentColor = computed(() => rgbParts.value ? `rgb(${rgbParts.value})` : 'var(--dark-3)')
const accentSoftColor = computed(() => rgbParts.value ? `rgba(${rgbParts.value}, 0.35)` : 'var(--light-3)')
const accentBgColor = computed(() => rgbParts.value ? `rgba(${rgbParts.value}, 0.12)` : 'var(--light-2)')

const linkedEntity = computed(() => {
  if (!schema.entityId) {
    return null
  }

  return (workflow.value.schemas ?? []).find((s) => s.id === schema.entityId && s.type === SchemaType.ENTITY)
})

const onBlurName = (e) => {
  blurReadModelName(e, schema.id)
}

const onSelectEntity = (entity) => {
  selectSourceEntity(schema.id, entity?.id || null)
}

const onAddField = () => {
  addSchemaField(schema.id)
}

const schemaDescriptionInvalid = ref(false)
const schemaDescriptionLength = ref(0)

const checkSchemaDescriptionLength = (event) => {
  schemaDescriptionLength.value = (event.target.innerText || '').length
  schemaDescriptionInvalid.value = schemaDescriptionLength.value > MAX_DESCRIPTION_LENGTH
}

const onPasteSchemaDescription = (event) => {
  copyPasteHelpers.customPasteHandler(event)
  checkSchemaDescriptionLength(event)
}

const onBlurSchemaDescription = async (event) => {
  const newDescription = (event.target.innerText || '').trim()
  const currentDescription = schema.description || ''

  if (newDescription.length > MAX_DESCRIPTION_LENGTH) {
    store.commit('snackbar/showMessage', {
      content: `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters`,
      timeout: 4000,
      color: 'red'
    }, { root: true })
    event.target.innerText = currentDescription
    schemaDescriptionInvalid.value = false

    return
  }

  schemaDescriptionInvalid.value = false
  await blurReadModelDescription(event, schema.id)
}

const restoreSchemaDescription = (event) => {
  event.target.innerText = schema.description || ''
  schemaDescriptionInvalid.value = false
  event.target.blur()
}
</script>

<style scoped>
.query-table-container {
  border: 1px solid var(--light-3) !important;
}

.query-toolbar {
  background-color: var(--light-1);
  border-bottom: 1px solid var(--light-3);
}

:deep(.entity-chip.v-chip) {
  height: 20px;
  font-size: 12px;
}

.cardinality-toggle :deep(.v-btn-group.v-btn-toggle .v-btn) {
  padding-top: 0;
  padding-bottom: 0;
}

.query-description {
  margin: 4px 16px;
  padding: 6px 8px;
  max-width: 90ch;
  font-size: 12px;
  font-style: italic;
  color: var(--text-light);
  white-space: pre-wrap;
  outline: none;
  cursor: text;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: border-color 0.2s;
}

.query-description:focus {
  font-style: normal;
  color: var(--text-dark);
}

.query-description--empty::before {
  content: attr(data-placeholder);
  color: var(--dark-1);
}

.query-description--empty:focus::before {
  content: '';
}

.query-description--invalid,
.query-description--invalid:focus {
  border-color: #ED1C0C;
}

.query-description-error {
  padding: 0 24px 8px 24px;
  font-size: 11px;
  color: #ED1C0C;
}

.query-fields-table :deep(thead th) {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-dark);
  padding: 6px 16px !important;
  height: 36px !important;
  background-color: var(--query-accent-bg) !important;
  border-bottom: 1px solid var(--query-accent-soft) !important;
}

.query-fields-table :deep(tbody td) {
  padding: 6px 16px !important;
  height: 40px !important;
  font-size: 13px;
}

.query-fields-table :deep(tbody tr:hover) {
  background-color: var(--light-2);
}

.query-fields-table :deep(td:focus),
.query-fields-table :deep(tr > td:focus-within) {
  outline: none !important;
}

.query-fields-table :deep(.quick-rename-input[contenteditable="true"]:not(.invalid):focus) {
  outline: 1px solid var(--query-accent-soft) !important;
  background-color: var(--light-1);
}

.query-fields-table :deep(.quick-rename-input.invalid[contenteditable="true"]:focus) {
  outline: 2px solid #ED1C0C !important;
}

.name-header {
  width: 240px;
  max-width: 240px;
}

.description-header {
  width: 320px;
  max-width: 320px;
}

.entity-header {
  width: 220px;
}

.actions-header {
  width: 48px;
  text-align: right;
  padding-right: 8px !important;
}

.query-fields-table :deep(tbody td.add-subfield-cell) {
  padding: 0 !important;
  height: 0 !important;
  border-bottom: none !important;
}
</style>
